import { NavItem } from '../shared/api/types/interfaces';
import { RailWorld } from './nav-data.constant';

/**
 * Who is looking at the rail: the one input to `visibleRailWorlds`. The rail passes the signed-in
 * user (`PermissionService`); the back-office user page's menu-access preview (NBO-2547) passes the
 * roles being edited — so "Opens N of M screens" is, by construction, what that user's rail shows.
 */
export interface NavViewer {
  /** Any-of: true when the viewer holds at least one of the roles with an access other than None. */
  hasAnyRole(role: NonNullable<NavItem['role']>): boolean;
  isPlatformStaff(): boolean;
  /** `environment.environment` — sections and items may be limited to some environments. */
  environment: string;
}

/**
 * The rail tree as one viewer sees it (NBO-2331 rules, moved here in NBO-2547): a world, a section
 * and an item each pass their own role gate (any-of) and environment list, staff-only items need a
 * platform-staff viewer, sections keep only their visible items and worlds only their non-empty
 * sections.
 */
export function visibleRailWorlds(worlds: readonly RailWorld[], viewer: NavViewer): RailWorld[] {
  return worlds
    .map((world) => ({
      ...world,
      sections: world.sections
        .filter((section) => envOk(section.env, viewer) && roleOk(section.role, viewer))
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => isRailItemVisible(item, viewer))
        }))
        .filter((section) => section.items.length > 0)
    }))
    .filter((world) => roleOk(world.role, viewer) && world.sections.length > 0);
}

/** The item's own gates only (role, environment, staff) — the section and world gates come on top. */
export function isRailItemVisible(item: NavItem, viewer: NavViewer): boolean {
  return envOk(item.env, viewer) && roleOk(item.role, viewer) && (!item.staffOnly || viewer.isPlatformStaff());
}

function roleOk(role: NavItem['role'] | undefined, viewer: NavViewer): boolean {
  return !role || viewer.hasAnyRole(role);
}

function envOk(env: readonly string[] | undefined, viewer: NavViewer): boolean {
  return !env || env.includes(viewer.environment);
}
